/**
 * The deferred "send this role's first-round invitations" action.
 *
 * ONE countdown for the whole batch, because the recruiter confirmed once. What
 * it then does is N separate sends — one call per candidate, each with the
 * recipient resolved from Lever by the proxy — never one message with N
 * recipients: a candidate must never see who else is being interviewed.
 *
 * Sends run SEQUENTIALLY, not in parallel. Each one is a Gmail write with the
 * recruiter's own token, and a burst of eight is the shape Gmail rate-limits;
 * a throttled batch would fail halfway with no clear record of who got mail.
 *
 * Partial failure is reported as such. Every send that succeeded has already
 * left, so the toast names the failures and the board keeps the ones that got
 * through marked as handled — the recruiter must be able to retry ONLY the rest.
 */
import { showToast } from '@finn-auto/design-system/atoms/toast';
import * as api from '../../services/taActionBoardApi';
import type { FirstInvitePreviewCandidate } from '../../services/firstInviteApi';
import type { RoleBoard } from '../../types/taActionBoard';
import type { PendingAction } from './usePendingActions';

export function bulkInviteAction({
  role,
  batch,
  gmailToken,
  unmarkHandled,
  onSent,
}: {
  role: RoleBoard;
  batch: FirstInvitePreviewCandidate[];
  gmailToken: string | null;
  unmarkHandled: (opportunityId: string) => void;
  /** Called with the ids that actually went out, so the board can refresh them. */
  onSent?: (opportunityIds: string[]) => void;
}): Omit<PendingAction, 'secondsLeft'> {
  const label =
    batch.length === 1
      ? `Inviting ${batch[0].name}`
      : `Sending ${batch.length} first-interview invitations for ${role.roleTitle}`;

  return {
    // Keyed by ROLE: a second confirmation for the same role supersedes the
    // first (usePendingActions runs its undo), which is the right behaviour for
    // a recruiter who realises they picked the wrong people and re-does it.
    id: `first-invite:${role.postingId}`,
    label,
    // Marked handled on confirm, sent ten seconds later — so these markers must
    // not reach the board cache until the send has actually happened.
    affects: batch.map((c) => c.opportunityId),
    run: () => {
      void (async () => {
        const sent: string[] = [];
        const failed: { name: string; why: string }[] = [];
        const bryqFailed: { name: string; why: string }[] = [];
        let bryqSent = 0;
        for (const candidate of batch) {
          try {
            const r = await api.sendCandidateEmail(
              {
                opportunityId: candidate.opportunityId,
                subject: candidate.subject || '',
                body: candidate.body || '',
                kind: 'first-invite',
                roleTitle: role.roleTitle,
                stageName: candidate.stageName,
                // Ids, so the board's own "already invited?" check keys on the
                // stage rather than on a renameable stage name.
                postingId: role.postingId,
                stageId: candidate.stageId,
                bryq: candidate.bryq ?? null,
              },
              gmailToken
            );
            sent.push(candidate.opportunityId);
            if (r.bryq?.ok) bryqSent += 1;
            if (r.bryq && !r.bryq.ok) bryqFailed.push({ name: candidate.name, why: r.bryq.error || 'unknown error' });
          } catch (e) {
            const why = e instanceof Error && e.message ? e.message : 'unknown error';
            failed.push({ name: candidate.name, why });
            // This one never left — put the card back so it can be retried.
            unmarkHandled(candidate.opportunityId);
          }
        }
        onSent?.(sent);
        // The invites themselves went out; a Bryq step that did not is its own, separate warning.
        if (bryqFailed.length > 0) {
          showToast({
            status: 'warning',
            description: `Bryq was NOT sent to ${bryqFailed.map((f) => `${f.name} (${f.why})`).join('; ')} — send it from the 🧠 chip.`,
          });
        }
        if (failed.length === 0) {
          showToast({
            status: 'success',
            // Counted from the outcomes, not from what was asked: a failed Bryq has its own warning.
            description: `${sent.length} invitation${sent.length === 1 ? '' : 's'} sent for ${role.roleTitle}${
              bryqSent > 0 ? ` (Bryq sent to ${bryqSent})` : ''
            }.`,
          });
          return;
        }
        showToast({
          status: sent.length > 0 ? 'warning' : 'error',
          description:
            sent.length > 0
              ? `${sent.length} sent, ${failed.length} failed: ${failed
                  .map((f) => `${f.name} (${f.why})`)
                  .join('; ')}. The failed ones are back on the list.`
              : `Nothing was sent: ${failed.map((f) => `${f.name} (${f.why})`).join('; ')}`,
        });
      })();
    },
    undo: () => {
      for (const candidate of batch) unmarkHandled(candidate.opportunityId);
      showToast({
        status: 'info',
        description: `Stopped — no invitation was sent for ${role.roleTitle}.`,
      });
    },
  };
}
